import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { useTranslation } from 'react-i18next';
import type { ListViewExtraProps } from '@/config/listViewExtras';
import { AccountRuleOrderDialog } from './AccountRuleOrderDialog';

/**
 * Toolbar action on the Account Derivation Rules grid: "Show evaluation order".
 *
 * Scoped by the picked rule rather than showing every rule at once — deliberately. Rules only
 * compete within their own (Legal Entity, Header Type) group, so one flat list across all of them
 * would imply a contest that does not exist, and would grow without bound as a tenant accumulates
 * rules. Picking a row names the group; the dialog then shows that group in evaluation order with
 * the picked rule highlighted.
 *
 * Disabled with an explanatory tooltip until exactly one row is selected, so the requirement is
 * discoverable rather than a dead button.
 */
export function AccountRuleOrderButton({ selectedIds }: ListViewExtraProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const ruleId = selectedIds.length === 1 ? selectedIds[0] : null;
  const label = t('Finance.MenuAction.AccountRuleOrder', { defaultValue: 'Show evaluation order' });

  const hint = ruleId
    ? label
    : selectedIds.length === 0
      ? t('Finance.AccountRuleOrder.PickOne', {
          defaultValue: 'Select a rule to see the order its group is evaluated in',
        })
      : t('Finance.AccountRuleOrder.PickExactlyOne', {
          defaultValue: 'Select a single rule — the order is shown for its group',
        });

  return (
    <>
      {/* A disabled MUI Button swallows pointer events, so the tooltip needs a wrapper to hear them. */}
      <Tooltip title={hint}>
        <span>
          <Button
            startIcon={<FormatListNumberedIcon />}
            disabled={!ruleId}
            onClick={() => setOpen(true)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
      {ruleId && (
        <AccountRuleOrderDialog entityId={ruleId} open={open} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
